// Styled .xlsx writer for formatted reports (fonts, fills, borders, number
// formats, formulas, merged cells, frozen panes, filters, conditional
// formatting, drop-down lists, print setup). Built on zip.js.
//
//   const wb = new StyledWorkbook();
//   const sh = wb.addSheet('Summary', { tabColor: '1F4E79' });
//   sh.setColumn(1, { width: 30 });
//   sh.set(1, 1, 'Title', { font: { bold: true, size: 16 } });
//   sh.set(2, 2, 12.5, { numFmt: '#,##0.00' });
//   sh.formula(3, 2, 'B2*2', 25, { numFmt: '#,##0.00' });
//   const bytes = await wb.toBytes();
//
// Rows and columns are 1-based. Formulas are written with their value
// already worked out (so the file shows numbers even before Excel
// recalculates) and Excel is told to recalculate when the file opens.

import { createZip } from './zip.js';

const XML_HEADER = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
const NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const NS_R = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

export function xmlEscape(value) {
  return String(value)
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** 1 -> A, 27 -> AA */
export function colName(col) {
  let n = col;
  let letters = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
}

export const cellRef = (row, col) => `${colName(col)}${row}`;

const argb = (hex) => `FF${String(hex).replace(/^#/, '').toUpperCase()}`;

// ---- Styles -------------------------------------------------------------------

function fontXml(font = {}) {
  return `<font>${font.bold ? '<b/>' : ''}${font.italic ? '<i/>' : ''}${font.underline ? '<u/>' : ''}<sz val="${font.size || 10}"/>${
    font.color ? `<color rgb="${argb(font.color)}"/>` : ''
  }<name val="${xmlEscape(font.name || 'Calibri')}"/><family val="2"/></font>`;
}

function fillXml(fill) {
  if (!fill) return '<fill><patternFill patternType="none"/></fill>';
  return `<fill><patternFill patternType="solid"><fgColor rgb="${argb(fill)}"/><bgColor indexed="64"/></patternFill></fill>`;
}

/** border: 'thin' | 'medium' | { top, bottom, left, right: 'thin'|'medium'|'double' }, borderColor */
function borderXml(border, color = 'B7C2D0') {
  if (!border) return '<border><left/><right/><top/><bottom/><diagonal/></border>';
  const sides = typeof border === 'string' ? { left: border, right: border, top: border, bottom: border } : border;
  const side = (name) =>
    sides[name] ? `<${name} style="${sides[name]}"><color rgb="${argb(color)}"/></${name}>` : `<${name}/>`;
  return `<border>${side('left')}${side('right')}${side('top')}${side('bottom')}<diagonal/></border>`;
}

class StyleRegistry {
  constructor() {
    this.numFmts = new Map(); // code -> id
    this.fonts = [fontXml({})];
    this.fills = [fillXml(null), '<fill><patternFill patternType="gray125"/></fill>'];
    this.borders = [borderXml(null)];
    this.xfs = ['<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>'];
    this.xfIndex = new Map();
    this.dxfs = [];
    this.dxfIndex = new Map();
  }

  static index(list, xml) {
    const found = list.indexOf(xml);
    if (found >= 0) return found;
    list.push(xml);
    return list.length - 1;
  }

  numFmtId(code) {
    if (!code) return 0;
    const builtIn = { General: 0, '0': 1, '0.00': 2, '#,##0': 3, '#,##0.00': 4, '@': 49 };
    if (builtIn[code] !== undefined) return builtIn[code];
    if (!this.numFmts.has(code)) this.numFmts.set(code, 164 + this.numFmts.size);
    return this.numFmts.get(code);
  }

  /** Style object -> cellXfs index. */
  id(style) {
    if (!style) return 0;
    if (typeof style === 'number') return style;
    const key = JSON.stringify(style);
    if (this.xfIndex.has(key)) return this.xfIndex.get(key);
    const fontId = StyleRegistry.index(this.fonts, fontXml(style.font));
    const fillId = StyleRegistry.index(this.fills, fillXml(style.fill));
    const borderId = StyleRegistry.index(this.borders, borderXml(style.border, style.borderColor));
    const numFmtId = this.numFmtId(style.numFmt);
    const align = style.align || {};
    const alignment =
      align.h || align.v || align.wrap || align.indent
        ? `<alignment${align.h ? ` horizontal="${align.h}"` : ''}${align.v ? ` vertical="${align.v}"` : ''}${
            align.wrap ? ' wrapText="1"' : ''
          }${align.indent ? ` indent="${align.indent}"` : ''}/>`
        : '';
    const xml = `<xf numFmtId="${numFmtId}" fontId="${fontId}" fillId="${fillId}" borderId="${borderId}" xfId="0"${
      numFmtId ? ' applyNumberFormat="1"' : ''
    } applyFont="1" applyFill="1" applyBorder="1"${alignment ? ' applyAlignment="1">' + alignment + '</xf>' : '/>'}`;
    this.xfs.push(xml);
    const index = this.xfs.length - 1;
    this.xfIndex.set(key, index);
    return index;
  }

  /** Conditional-format look: { font: { bold, color }, fill } -> dxfs index. */
  dxf(look) {
    const key = JSON.stringify(look);
    if (this.dxfIndex.has(key)) return this.dxfIndex.get(key);
    const font = look.font
      ? `<font>${look.font.bold ? '<b/>' : ''}${look.font.color ? `<color rgb="${argb(look.font.color)}"/>` : ''}</font>`
      : '';
    const fill = look.fill ? `<fill><patternFill patternType="solid"><bgColor rgb="${argb(look.fill)}"/></patternFill></fill>` : '';
    this.dxfs.push(`<dxf>${font}${fill}</dxf>`);
    const index = this.dxfs.length - 1;
    this.dxfIndex.set(key, index);
    return index;
  }

  xml() {
    const numFmts = [...this.numFmts.entries()]
      .map(([code, id]) => `<numFmt numFmtId="${id}" formatCode="${xmlEscape(code)}"/>`)
      .join('');
    return `${XML_HEADER}<styleSheet xmlns="${NS}">${
      this.numFmts.size ? `<numFmts count="${this.numFmts.size}">${numFmts}</numFmts>` : ''
    }<fonts count="${this.fonts.length}">${this.fonts.join('')}</fonts><fills count="${this.fills.length}">${this.fills.join(
      '',
    )}</fills><borders count="${this.borders.length}">${this.borders.join(
      '',
    )}</borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="${
      this.xfs.length
    }">${this.xfs.join('')}</cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles><dxfs count="${
      this.dxfs.length
    }">${this.dxfs.join('')}</dxfs></styleSheet>`;
  }
}

// ---- Sheets -------------------------------------------------------------------

export class StyledSheet {
  constructor(workbook, name, options = {}) {
    this.workbook = workbook;
    this.name = name;
    this.options = options;
    this.rows = new Map(); // row -> Map(col -> xml)
    this.heights = new Map();
    this.columns = new Map(); // col -> { width, hidden }
    this.merges = [];
    this.conditional = [];
    this.validations = [];
    this.freeze = null;
    this.filter = null;
    this.printTitles = null;
    this.maxRow = 0;
    this.maxCol = 0;
  }

  setColumn(col, { width, hidden } = {}) {
    this.columns.set(col, { width, hidden });
    return this;
  }

  setRowHeight(row, height) {
    this.heights.set(row, height);
    return this;
  }

  put(row, col, xml) {
    if (!this.rows.has(row)) this.rows.set(row, new Map());
    this.rows.get(row).set(col, xml);
    this.maxRow = Math.max(this.maxRow, row);
    this.maxCol = Math.max(this.maxCol, col);
  }

  /** Value cell: numbers as numbers, everything else as text. Blank values keep the style only. */
  set(row, col, value, style) {
    const ref = cellRef(row, col);
    const s = this.workbook.styles.id(style);
    if (value === null || value === undefined || value === '') {
      if (s) this.put(row, col, `<c r="${ref}" s="${s}"/>`);
      return this;
    }
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return this;
      this.put(row, col, `<c r="${ref}" s="${s}"><v>${value}</v></c>`);
      return this;
    }
    this.put(row, col, `<c r="${ref}" t="inlineStr" s="${s}"><is><t xml:space="preserve">${xmlEscape(value)}</t></is></c>`);
    return this;
  }

  /** Formula cell (without the leading "="); cached = its worked-out value. */
  formula(row, col, formula, cached, style) {
    const ref = cellRef(row, col);
    const s = this.workbook.styles.id(style);
    const f = `<f>${xmlEscape(formula)}</f>`;
    if (typeof cached === 'number' && Number.isFinite(cached)) {
      this.put(row, col, `<c r="${ref}" s="${s}">${f}<v>${cached}</v></c>`);
    } else if (cached === null || cached === undefined) {
      this.put(row, col, `<c r="${ref}" s="${s}">${f}</c>`);
    } else {
      this.put(row, col, `<c r="${ref}" t="str" s="${s}">${f}<v>${xmlEscape(cached)}</v></c>`);
    }
    return this;
  }

  /** Merge a block and style every cell in it (so borders show all round). */
  merge(row1, col1, row2, col2, value, style) {
    for (let r = row1; r <= row2; r += 1) {
      for (let c = col1; c <= col2; c += 1) this.set(r, c, r === row1 && c === col1 ? value : null, style);
    }
    if (row1 !== row2 || col1 !== col2) this.merges.push(`${cellRef(row1, col1)}:${cellRef(row2, col2)}`);
    return this;
  }

  freezeAt(row, col = 1) {
    this.freeze = { row, col };
    return this;
  }

  autoFilter(row1, col1, row2, col2) {
    this.filter = { row1, col1, row2: Math.max(row1, row2), col2 };
    return this;
  }

  /** Header rows repeated on every printed page. */
  repeatRows(row1, row2 = row1) {
    this.printTitles = { row1, row2 };
    return this;
  }

  /** rule: { type: 'cellIs', operator: 'equal'|'lessThan'|..., formula } or { type: 'expression', formula }; look: dxf look. */
  addConditional(sqref, rule, look) {
    this.conditional.push({ sqref, rule, dxfId: this.workbook.styles.dxf(look) });
    return this;
  }

  /** Drop-down list (values must not contain commas; total under 255 characters). */
  addList(sqref, values) {
    this.validations.push({ sqref, values });
    return this;
  }

  xml() {
    const out = [`${XML_HEADER}<worksheet xmlns="${NS}" xmlns:r="${NS_R}">`];
    const { tabColor, landscape, fitWidth } = this.options;
    out.push(
      `<sheetPr>${tabColor ? `<tabColor rgb="${argb(tabColor)}"/>` : ''}${fitWidth ? '<pageSetUpPr fitToPage="1"/>' : ''}</sheetPr>`,
    );
    out.push(`<dimension ref="A1:${cellRef(Math.max(1, this.maxRow), Math.max(1, this.maxCol))}"/>`);
    let pane = '';
    if (this.freeze && (this.freeze.row > 1 || this.freeze.col > 1)) {
      const ySplit = this.freeze.row - 1;
      const xSplit = this.freeze.col - 1;
      const activePane = ySplit && xSplit ? 'bottomRight' : ySplit ? 'bottomLeft' : 'topRight';
      pane = `<pane${xSplit ? ` xSplit="${xSplit}"` : ''}${ySplit ? ` ySplit="${ySplit}"` : ''} topLeftCell="${cellRef(
        this.freeze.row,
        this.freeze.col,
      )}" activePane="${activePane}" state="frozen"/><selection pane="${activePane}" activeCell="${cellRef(
        this.freeze.row,
        this.freeze.col,
      )}" sqref="${cellRef(this.freeze.row, this.freeze.col)}"/>`;
    }
    out.push(
      `<sheetViews><sheetView workbookViewId="0"${this.options.showGrid === false ? ' showGridLines="0"' : ''}${
        this.options.zoom ? ` zoomScale="${this.options.zoom}"` : ''
      }${this.workbook.sheets[0] === this ? ' tabSelected="1"' : ''}>${pane}</sheetView></sheetViews>`,
    );
    out.push('<sheetFormatPr defaultRowHeight="15"/>');
    if (this.columns.size) {
      out.push('<cols>');
      [...this.columns.entries()]
        .sort((a, b) => a[0] - b[0])
        .forEach(([col, { width, hidden }]) =>
          out.push(
            `<col min="${col}" max="${col}" width="${width || 10}" customWidth="1"${hidden ? ' hidden="1"' : ''}/>`,
          ),
        );
      out.push('</cols>');
    }
    out.push('<sheetData>');
    const rowNumbers = new Set([...this.rows.keys(), ...this.heights.keys()]);
    [...rowNumbers]
      .sort((a, b) => a - b)
      .forEach((row) => {
        const cells = this.rows.get(row);
        const height = this.heights.get(row);
        out.push(`<row r="${row}"${height ? ` ht="${height}" customHeight="1"` : ''}>`);
        if (cells) [...cells.entries()].sort((a, b) => a[0] - b[0]).forEach(([, xml]) => out.push(xml));
        out.push('</row>');
      });
    out.push('</sheetData>');
    if (this.filter) {
      out.push(`<autoFilter ref="${cellRef(this.filter.row1, this.filter.col1)}:${cellRef(this.filter.row2, this.filter.col2)}"/>`);
    }
    if (this.merges.length) {
      out.push(`<mergeCells count="${this.merges.length}">${this.merges.map((m) => `<mergeCell ref="${m}"/>`).join('')}</mergeCells>`);
    }
    let priority = 1;
    this.conditional.forEach(({ sqref, rule, dxfId }) => {
      const body =
        rule.type === 'cellIs'
          ? `<cfRule type="cellIs" dxfId="${dxfId}" priority="${priority}" operator="${rule.operator}"><formula>${xmlEscape(rule.formula)}</formula></cfRule>`
          : `<cfRule type="expression" dxfId="${dxfId}" priority="${priority}"><formula>${xmlEscape(rule.formula)}</formula></cfRule>`;
      priority += 1;
      out.push(`<conditionalFormatting sqref="${sqref}">${body}</conditionalFormatting>`);
    });
    if (this.validations.length) {
      out.push(`<dataValidations count="${this.validations.length}">`);
      this.validations.forEach(({ sqref, values }) =>
        out.push(
          `<dataValidation type="list" allowBlank="1" showErrorMessage="0" sqref="${sqref}"><formula1>"${xmlEscape(
            values.join(','),
          )}"</formula1></dataValidation>`,
        ),
      );
      out.push('</dataValidations>');
    }
    out.push('<printOptions horizontalCentered="0"/>');
    out.push('<pageMargins left="0.3" right="0.3" top="0.5" bottom="0.5" header="0.3" footer="0.3"/>');
    out.push(
      `<pageSetup paperSize="9"${landscape ? ' orientation="landscape"' : ''}${fitWidth ? ' fitToWidth="1" fitToHeight="0"' : ''}/>`,
    );
    out.push('</worksheet>');
    return out.join('');
  }
}

export class StyledWorkbook {
  constructor() {
    this.styles = new StyleRegistry();
    this.sheets = [];
    this.used = new Set();
  }

  addSheet(name, options) {
    let base = String(name || 'Sheet').replace(/[[\]:*?/\\]/g, ' ').trim().slice(0, 31) || 'Sheet';
    let candidate = base;
    let n = 2;
    while (this.used.has(candidate.toLowerCase())) {
      const suffix = ` (${n})`;
      candidate = base.slice(0, 31 - suffix.length) + suffix;
      n += 1;
    }
    this.used.add(candidate.toLowerCase());
    const sheet = new StyledSheet(this, candidate, options);
    this.sheets.push(sheet);
    return sheet;
  }

  async toBytes() {
    const quoted = (name) => `'${xmlEscape(name).replace(/'/g, "''")}'`;
    const names = [];
    this.sheets.forEach((sheet, i) => {
      if (sheet.filter) {
        const f = sheet.filter;
        names.push(
          `<definedName name="_xlnm._FilterDatabase" localSheetId="${i}" hidden="1">${quoted(sheet.name)}!$${colName(f.col1)}$${f.row1}:$${colName(
            f.col2,
          )}$${f.row2}</definedName>`,
        );
      }
      if (sheet.printTitles) {
        names.push(
          `<definedName name="_xlnm.Print_Titles" localSheetId="${i}">${quoted(sheet.name)}!$${sheet.printTitles.row1}:$${sheet.printTitles.row2}</definedName>`,
        );
      }
    });
    const sheetXml = this.sheets.map((sheet) => sheet.xml()); // registers styles first
    const files = [
      {
        name: '[Content_Types].xml',
        data: `${XML_HEADER}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>${this.sheets
          .map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`)
          .join('')}</Types>`,
      },
      {
        name: '_rels/.rels',
        data: `${XML_HEADER}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
      },
      {
        name: 'xl/workbook.xml',
        data: `${XML_HEADER}<workbook xmlns="${NS}" xmlns:r="${NS_R}"><workbookPr/><bookViews><workbookView activeTab="0"/></bookViews><sheets>${this.sheets
          .map((s, i) => `<sheet name="${xmlEscape(s.name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
          .join('')}</sheets>${names.length ? `<definedNames>${names.join('')}</definedNames>` : ''}<calcPr calcId="191029" fullCalcOnLoad="1"/></workbook>`,
      },
      {
        name: 'xl/_rels/workbook.xml.rels',
        data: `${XML_HEADER}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${this.sheets
          .map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`)
          .join('')}<Relationship Id="rId${this.sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`,
      },
      { name: 'xl/styles.xml', data: this.styles.xml() },
      ...sheetXml.map((xml, i) => ({ name: `xl/worksheets/sheet${i + 1}.xml`, data: xml })),
    ];
    return createZip(files);
  }
}
